import { useState, useCallback, useEffect, useRef } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";
import { useOcelMetadata } from "../../../../stores/OcelContext";
import { useGlobalFilters } from "../../../../stores/GlobalFilterContext/GlobalFilterContext";

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const normalizeSelectedDateForGranularity = (selectedDate, granularity) => {
  if (!selectedDate || granularity === "day") return selectedDate;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(selectedDate);
  if (!m) return selectedDate;

  const yearStr = m[1];
  const month = Number(m[2]);
  const day = Number(m[3]);
  const year = Number(yearStr);
  const pad2 = (n) => String(n).padStart(2, "0");

  if (granularity === "month") {
    return `${yearStr}-${pad2(month)}-01`;
  }
  if (granularity === "year") {
    return `${yearStr}-01-01`;
  }
  if (granularity === "week") {
    const dt = new Date(Date.UTC(year, month - 1, day));
    const mondayOffset = (dt.getUTCDay() + 6) % 7;
    dt.setUTCDate(dt.getUTCDate() - mondayOffset);
    return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
  }
  return selectedDate;
};


const useTransitionTimeLineChart = (activeFile, initialSettings = null) => {
  const api = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const { filterState } = useGlobalFilters();
  const configKey = `transitionTimeLineChartConfig_${activeFile || "default"}`;
  const viewKey = `transitionTimeLineChartView_${activeFile || "default"}`;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [datasetLastDate, setDatasetLastDate] = useState(null);

  const [objectTypes, setObjectTypes] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  // TileConfig (persisted)
  const [title, setTitle] = useState("Transition Time Line Chart");
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [cycleStartActivity] = useState("cycle_start");
  const [cycleEndActivity] = useState("cycle_end");
  const [configuredResources, setConfiguredResources] = useState([]);
  const [toleranceThreshold, setToleranceThreshold] = useState(60);
  const [enabledGranularities, setEnabledGranularities] = useState([
    "day",
    "week",
    "month",
    "year",
  ]);
  const [resourceColumn, setResourceColumn] = useState("ressource");

  // ViewState (persisted)
  const [viewResources, setViewResources] = useState([]);
  const [granularity, setGranularity] = useState("day");
  const [selectedDate, setSelectedDate] = useState(yesterdayISO());
  const [viewMode, setViewMode] = useState("granularity");
  const [dateRangeStart, setDateRangeStart] = useState(yesterdayISO());
  const [dateRangeEnd, setDateRangeEnd] = useState(yesterdayISO());

  const inFlightSignatureRef = useRef(null);
  const lastResolvedSignatureRef = useRef(null);
  const lastResolvedAtRef = useRef(0);
  const latestRequestIdRef = useRef(0);

  // Restore TileConfig
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(configKey);
      if (!raw) return;
      const cfg = JSON.parse(raw);
      if (typeof cfg.title === "string") setTitle(cfg.title);
      if (typeof cfg.selectedObjectType === "string")
        setSelectedObjectType(cfg.selectedObjectType);
      if (Array.isArray(cfg.configuredResources))
        setConfiguredResources(cfg.configuredResources);
      if (typeof cfg.toleranceThreshold === "number")
        setToleranceThreshold(cfg.toleranceThreshold);
      if (
        Array.isArray(cfg.enabledGranularities) &&
        cfg.enabledGranularities.length > 0
      )
        setEnabledGranularities(cfg.enabledGranularities);
    } catch {
      /* no-op */
    }
  }, [configKey]);

  // Restore ViewState
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(viewKey);
      if (!raw) return;
      const vs = JSON.parse(raw);
      if (typeof vs.granularity === "string") setGranularity(vs.granularity);
      if (Array.isArray(vs.viewResources)) setViewResources(vs.viewResources);
      if (typeof vs.selectedDate === "string" && DATE_RE.test(vs.selectedDate))
        setSelectedDate(vs.selectedDate);
      if (vs.viewMode === "granularity" || vs.viewMode === "daterange")
        setViewMode(vs.viewMode);
      if (
        typeof vs.dateRangeStart === "string" &&
        DATE_RE.test(vs.dateRangeStart)
      )
        setDateRangeStart(vs.dateRangeStart);
      if (typeof vs.dateRangeEnd === "string" && DATE_RE.test(vs.dateRangeEnd))
        setDateRangeEnd(vs.dateRangeEnd);
    } catch {
      /* no-op */
    }
  }, [viewKey]);

  // Restore from centralised config (overrides localStorage on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    const s = initialSettings;
    if (s.title !== undefined) setTitle(s.title);
    if (s.selectedObjectType !== undefined) setSelectedObjectType(s.selectedObjectType);
    if (s.configuredResources !== undefined) setConfiguredResources(s.configuredResources);
    if (s.toleranceThreshold !== undefined) setToleranceThreshold(s.toleranceThreshold);
    if (s.enabledGranularities !== undefined) setEnabledGranularities(s.enabledGranularities);
    if (s.viewResources !== undefined) setViewResources(s.viewResources);
    if (s.granularity !== undefined) setGranularity(s.granularity);
    if (s.selectedDate !== undefined) setSelectedDate(s.selectedDate);
    if (s.viewMode !== undefined) setViewMode(s.viewMode);
    if (s.dateRangeStart !== undefined) setDateRangeStart(s.dateRangeStart);
    if (s.dateRangeEnd !== undefined) setDateRangeEnd(s.dateRangeEnd);
  }, []);

  // Persist TileConfig
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        configKey,
        JSON.stringify({
          title,
          selectedObjectType,
          configuredResources,
          toleranceThreshold,
          enabledGranularities,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    configKey,
    title,
    selectedObjectType,
    configuredResources,
    toleranceThreshold,
    enabledGranularities,
  ]);

  // Persist ViewState
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        viewKey,
        JSON.stringify({
          granularity,
          viewResources,
          selectedDate,
          viewMode,
          dateRangeStart,
          dateRangeEnd,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    viewKey,
    granularity,
    viewResources,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
  ]);

  // Reactive metadata sync from OcelContext
  useEffect(() => {
    if (!metadata?.object_types) return;
    const hasActiveFilters = Object.values(filterState).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );
    if (hasActiveFilters) return;

    const types = Object.keys(metadata.object_types);
    setObjectTypes(types);

    if (metadata.resource_values) {
      setAvailableResources(metadata.resource_values);
    }
    if (metadata.resource_column) {
      setResourceColumn(metadata.resource_column);
    }

    if (metadata.date_range?.end) {
      const lastDate = metadata.date_range.end.slice(0, 10);
      setDatasetLastDate(lastDate);
      setSelectedDate((prev) => (prev > lastDate ? lastDate : prev));
    }

    setIsMetadataLoaded(true);
  }, [metadata, filterState, activeFile]);

  // Auto-select first object type
  useEffect(() => {
    if (!selectedObjectType && objectTypes.length > 0) {
      setSelectedObjectType(objectTypes[0]);
    }
  }, [objectTypes, selectedObjectType]);

  const fetchMetadata = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return false;
    }

    const hasActiveFilters = Object.values(filterState).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );
    if (!hasActiveFilters) {
      setIsMetadataLoaded(true);
      return true;
    }

    setLoading(true);
    setError(null);

    try {
      const [objectTypesResponse, resourceResponse] = await Promise.all([
        api.post(
          "/filtered-object-types",
          { file_type: activeFile },
          {},
          "Object Types",
        ),
        api.getRaw(
          "/ocel/resource-values",
          { file_type: activeFile },
          {},
          "Resource Values",
        ),
      ]);
      const types = objectTypesResponse.objectTypes || [];
      setObjectTypes(types);
      if (!selectedObjectType && types.length > 0) {
        setSelectedObjectType(types[0]);
      }

      const resources = resourceResponse.values || [];
      setAvailableResources(resources);
      if (resourceResponse.used_column) {
        setResourceColumn(resourceResponse.used_column.replace(/^ocel:/, ""));
      }

      setIsMetadataLoaded(true);
      return true;
    } catch (err) {
      setError(err.message || "Failed to load metadata");
      setIsMetadataLoaded(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeFile, api, filterState, selectedObjectType]);

  const fetchChartData = useCallback(
    async (overrides = {}) => {
      const effectiveConfiguredResources =
        overrides.configuredResources ?? configuredResources;
      const effectiveViewResources = overrides.viewResources ?? viewResources;
      const effectiveResources =
        effectiveViewResources.length > 0
          ? effectiveViewResources
          : effectiveConfiguredResources;
      const effectiveSelectedObjectType =
        overrides.selectedObjectType ?? selectedObjectType;
      const effectiveToleranceThreshold =
        overrides.toleranceThreshold ?? toleranceThreshold;
      const effectiveGranularity = overrides.granularity ?? granularity;
      const effectiveEnabledGranularities =
        overrides.enabledGranularities ?? enabledGranularities;
      const effectiveSelectedDate = overrides.selectedDate ?? selectedDate;
      const normalizedGranularityDate = normalizeSelectedDateForGranularity(
        effectiveSelectedDate,
        effectiveGranularity,
      );
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart =
        overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;

      if (!activeFile) {
        setError("No active file selected");
        return false;
      }
      if (!effectiveSelectedObjectType) {
        setError("Please select an object type");
        return false;
      }
      if (effectiveResources.length === 0) {
        setError("Please select at least one resource");
        return false;
      }
      const isDateRange = effectiveViewMode === "daterange";
      if (!isDateRange && !effectiveSelectedDate) {
        setError("Please select a date");
        return false;
      }
      if (isDateRange && (!effectiveDateRangeStart || !effectiveDateRangeEnd)) {
        setError("Please select start and end dates");
        return false;
      }

      const payload = {
        file_type: activeFile,
        resources: effectiveResources,
        tolerance_threshold_seconds: effectiveToleranceThreshold,
        cycle_start_column: cycleStartActivity,
        cycle_end_column: cycleEndActivity,
        production_order_column: effectiveSelectedObjectType,
        resource_column: resourceColumn,
        ...(isDateRange
          ? {
              period_start: effectiveDateRangeStart,
              period_end: effectiveDateRangeEnd,
              // Daterange mode buckets per calendar day across the range. "month"
              // is the granularity that produces day-floor buckets in the backend
              // (day → hour buckets, year → month buckets, week/month → day buckets);
              // picking "month" lets a multi-day range render one slot per day.
              granularity: "month",
              selected_date: effectiveDateRangeStart,
            }
          : {
              granularity: effectiveGranularity,
              selected_date: normalizedGranularityDate,
              enabled_granularities: effectiveEnabledGranularities,
            }),
      };

      const requestSignature = JSON.stringify({
        ...payload,
        resources: [...payload.resources].sort(),
        ...(payload.enabled_granularities
          ? { enabled_granularities: [...payload.enabled_granularities].sort() }
          : {}),
      });

      if (inFlightSignatureRef.current === requestSignature) return true;
      const now = Date.now();
      if (
        lastResolvedSignatureRef.current === requestSignature &&
        now - lastResolvedAtRef.current < 1000
      ) {
        return true;
      }

      setLoading(true);
      setError(null);
      const requestId = ++latestRequestIdRef.current;

      try {
        inFlightSignatureRef.current = requestSignature;
        const response = await api.post(
          "/kpi/transition-time-line-chart",
          payload,
          {},
          "Transition Time Line Chart",
        );
        if (requestId !== latestRequestIdRef.current) return true;
        setChartData(response);
        lastResolvedSignatureRef.current = requestSignature;
        lastResolvedAtRef.current = Date.now();
        return true;
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return false;
        setChartData(null);
        let msg = err?.message || "Failed to load chart data";
        if (msg.includes("404")) {
          msg = "Endpoint not found. Please check if the backend is running.";
        } else if (msg.includes("400")) {
          msg = "Invalid configuration. Please check your settings.";
        }
        setError(msg);
        return false;
      } finally {
        if (inFlightSignatureRef.current === requestSignature) {
          inFlightSignatureRef.current = null;
        }
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [
      activeFile,
      api,
      configuredResources,
      cycleEndActivity,
      cycleStartActivity,
      enabledGranularities,
      granularity,
      resourceColumn,
      selectedDate,
      selectedObjectType,
      toleranceThreshold,
      viewResources,
      viewMode,
      dateRangeStart,
      dateRangeEnd,
    ],
  );

  // Reset on file change
  useEffect(() => {
    setChartData(null);
    setError(null);
  }, [activeFile]);

  // Keep viewResources in sync with configuredResources
  useEffect(() => {
    setViewResources((prev) => {
      const allowed = prev.filter((r) => configuredResources.includes(r));
      if (allowed.length > 0) return allowed;
      return configuredResources;
    });
  }, [configuredResources]);

  return {
    loading,
    error,
    chartData,
    datasetLastDate,
    objectTypes,
    availableResources,
    isMetadataLoaded,
    // TileConfig
    title,
    selectedObjectType,
    configuredResources,
    toleranceThreshold,
    enabledGranularities,
    // ViewState
    viewResources,
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    // TileConfig setters
    setTitle,
    setSelectedObjectType,
    setConfiguredResources,
    setToleranceThreshold,
    setEnabledGranularities,
    // ViewState setters
    setViewResources,
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    // Actions
    fetchMetadata,
    fetchChartData,
  };
};

export { useTransitionTimeLineChart };
